import type { Arrangement, RoofHalfEdge } from "building-types/roofVolumes/roofVolumeTypes";
import type { PlaneBSP } from "geom/bspTypes";
import { DIST_EPSILON, ONE_MINUS_DIST_EPSILON } from "geom/core";
import type { Vec3 } from "geom/vec3";
import { vec3Add, vec3Cross, vec3Dist, vec3Dot, vec3Normalize, vec3Scale, vec3Sub } from "geom/vec3";
import { addVertexToArrangement, connectHalfs, removeHalfsFromArrangement } from "./arrangementUtils";
import {
  getDirection,
  getEdgeLength,
  getMostNext,
  getMostPrevious,
  getPlane,
  getPosition,
  isBidirectional,
  isReflex,
  mergeWithTwin,
  updateConvex,
} from "./halfEdgeUtils";
import { intersect3D, linePointDistance } from "./intersector";
import { isCoplanar, intersectLine, intersectPlanes } from "./planeUtils";
import { connectHalfEdges, createVertex } from "./vertexUtils";

// A propagation event.
interface PropagationEvent {
  halfA?: RoofHalfEdge;
  halfB?: RoofHalfEdge;
  position?: Vec3;
  straight?: boolean;
  distance: number;
  plane?: PlaneBSP;
}

interface Ray {
  half: RoofHalfEdge;
  bidirectional?: boolean;
}

// Compare two propagation events and pick the one that should happen first.
function compareEvents(a: PropagationEvent, b: PropagationEvent | null): PropagationEvent {
  // If one is invalid, pick the other one.
  if (!b || !b.position) {
    return a;
  }
  if (!a || !a.position) {
    return b;
  }
  // Pick the event with a lower z-value. Ignore numeric precision problems.
  if (a.position[2] < b.position[2] - DIST_EPSILON) {
    return a;
  }
  if (a.position[2] > b.position[2] + DIST_EPSILON) {
    return b;
  }
  // Pick the event with the shortest distance.
  if (a.distance < b.distance - DIST_EPSILON) {
    return a;
  }
  if (a.distance > b.distance + DIST_EPSILON) {
    return b;
  }
  // Prefer straight if they have the same distance.
  if (b.straight) {
    return b;
  }
  return a;
}

// Find a propagation event that combines the half-edges in a straight line.
// This only applies when the two half-edges are colinear.
function findStraightEvent(halfA: RoofHalfEdge, halfB: RoofHalfEdge): PropagationEvent | null {
  const [aPosX, aPosY, aPosZ] = getPosition(halfA);
  const [bPosX, bPosY, bPosZ] = getPosition(halfB);
  const [aDirX, aDirY, aDirZ] = getDirection(halfA);
  const [bDirX, bDirY, bDirZ] = getDirection(halfB);

  // Reject if A does not point towards B.
  const [diffX, diffY, diffZ] = vec3Sub(bPosX, bPosY, bPosZ, aPosX, aPosY, aPosZ);
  if (vec3Dot(aDirX, aDirY, aDirZ, diffX, diffY, diffZ) < 0) {
    return null;
  }

  // Check that the lines are parallel.
  const dot = isNaN(aDirX) || isNaN(bDirX) ? 0 : vec3Dot(aDirX, aDirY, aDirZ, bDirX, bDirY, bDirZ);
  if (Math.abs(dot) > ONE_MINUS_DIST_EPSILON) {
    // Check that the position of B is on line A.
    if (linePointDistance(aPosX, aPosY, aPosZ, aDirX, aDirY, aDirZ, bPosX, bPosY, bPosZ) < DIST_EPSILON) {
      const [posX, posY, posZ] = aPosZ < bPosZ ? [aPosX, aPosY, aPosZ] : [bPosX, bPosY, bPosZ];
      const distance = vec3Dist(aPosX, aPosY, aPosZ, bPosX, bPosY, bPosZ);
      return { halfA, halfB, straight: true, distance, position: [posX, posY, posZ] };
    }
  }
  return null;
}

// Find an intersection event between the two half-edges.
function findIntersectEvent(rayA: Ray, halfB: RoofHalfEdge): PropagationEvent {
  const halfA = rayA.half;
  const [aPosX, aPosY, aPosZ] = getPosition(halfA);
  const [bPosX, bPosY, bPosZ] = getPosition(halfB);
  const [aDirX, aDirY, aDirZ] = getDirection(halfA);
  const [bDirX, bDirY, bDirZ] = getDirection(halfB);
  // The two half-edges must be incomplete rays. Ignore otherwise.
  if (isNaN(aDirX) || isNaN(bDirX)) {
    return { distance: Infinity };
  }
  // Two half-edges should not originate from the same location.
  if (vec3Dist(aPosX, aPosY, aPosZ, bPosX, bPosY, bPosZ) < DIST_EPSILON) {
    return { distance: Infinity };
  }

  // Find the intersection point of the two rays.
  const [ix, iy, iz] = intersect3D(
    aPosX,
    aPosY,
    aPosZ,
    aDirX,
    aDirY,
    aDirZ,
    bPosX,
    bPosY,
    bPosZ,
    bDirX,
    bDirY,
    bDirZ,
    false,
  );
  if (isNaN(ix)) {
    return { distance: Infinity };
  }
  const [deltaX, deltaY, deltaZ] = vec3Sub(ix, iy, iz, aPosX, aPosY, aPosZ);
  let distance = vec3Dot(aDirX, aDirY, aDirZ, deltaX, deltaY, deltaZ);
  if (rayA.bidirectional) {
    distance = Math.abs(distance);
  }
  if (distance < 0) {
    return { distance: Infinity };
  }
  // Do not allow rays to be cast downwards.
  // Discard if the intersection point is below the starting points.
  if (iz < aPosZ - DIST_EPSILON) {
    return { distance: Infinity };
  }

  const lineDistance = linePointDistance(aPosX, aPosY, aPosZ, aDirX, aDirY, aDirZ, ix, iy, iz);
  if (lineDistance > DIST_EPSILON) {
    return { distance: Infinity };
  }
  return { position: [ix, iy, iz], distance, halfA, halfB };
}

// For a half-edge at the end of a face, first the positions of all the vertices of the face.
// Extend the first and last half-edge to where they intersect or some far distance.
function findFacePositions(half: RoofHalfEdge) {
  const faceHalfs: RoofHalfEdge[] = [];
  // Trace the face backwards.
  let prev: RoofHalfEdge | undefined = half;
  while (prev) {
    faceHalfs.push(prev);
    prev = prev.prev;
  }
  faceHalfs.reverse();
  const [pos0x, pos0y, pos0z] = getPosition(faceHalfs[0]);
  const [pos1x, pos1y, pos1z] = getPosition(faceHalfs[faceHalfs.length - 1]);
  const [neg0x, neg0y, neg0z] = getDirection(faceHalfs[0]);
  let [dir0x, dir0y, dir0z] = vec3Scale(neg0x, neg0y, neg0z, -1);
  let [dir1x, dir1y, dir1z] = getDirection(faceHalfs[faceHalfs.length - 1]);
  const positions: Vec3[] = [];
  faceHalfs.forEach((half, index) => {
    if (index === 0) {
      // Skip the first position. Its a duplicate of the last one.
      return;
    }
    const [px, py, pz] = getPosition(half);
    positions.push([px, py, pz]);
  });
  // Intersect the first and last half-edges.
  const [ix, iy, iz] = intersect3D(
    pos0x,
    pos0y,
    pos0z,
    dir0x,
    dir0y,
    dir0z,
    pos1x,
    pos1y,
    pos1z,
    dir1x,
    dir1y,
    dir1z,
    true,
  );

  if (!isNaN(ix)) {
    // If the rays intersect, add the intersection to the front and the back.
    positions.unshift([ix, iy, iz]);
    positions.push([ix, iy, iz]);
  } else if (vec3Dot(dir0x, dir0y, dir0z, dir1z, dir1y, dir1z) > -0.99) {
    // If rays are parallel in the same direction, do nothing.
    // If rays are parallel in the oppositie directions, move them out to a long distance.
    [dir0x, dir0y, dir0z] = vec3Scale(dir0x, dir0y, dir0z, 1e6);
    [dir1x, dir1y, dir1z] = vec3Scale(dir1x, dir1y, dir1z, 1e6);
    const [far0x, far0y, far0z] = vec3Add(dir0x, dir0y, dir0z, pos0x, pos0y, pos0z);
    const [far1x, far1y, far1z] = vec3Add(dir1x, dir1y, dir1z, pos1x, pos1y, pos1z);
    positions.unshift([far0x, far0y, far0z]);
    positions.push([far1x, far1y, far1z]);
  }
  return positions;
}

function isPointInsideFace(positions: number[][], planeB: PlaneBSP, ix: number, iy: number, iz: number): boolean {
  const { nX: nBx, nY: nBy, nZ: nBz } = planeB;
  // Find a horizontal ray on the face.
  let [rayX, rayY, rayZ] = vec3Cross(nBx, nBy, nBz, 0, 0, 1);
  [rayX, rayY, rayZ] = vec3Normalize(rayX, rayY, rayZ);

  // Move the intersection point down slightly. This fixes some numeric precision
  // issues when the intersection point is the same z value as some positions.
  const [i0x, i0y, i0z] = [ix, iy, iz - 0.01];

  // Find the first intersection point where the ray hits one side of the face.
  // The orientation of this side will tell us if we are inside the face or not.
  let firstIntersection = { distance: Infinity, inside: false };
  for (let i = 0; i < positions.length - 1; i++) {
    const [p0x, p0y, p0z] = positions[i];
    const [p1x, p1y, p1z] = positions[i + 1];
    // The positions must cross the intersection z-value.
    if (p0z < i0z !== p1z < i0z) {
      const [sideX, sideY, sideZ] = vec3Sub(p1x, p1y, p1z, p0x, p0y, p0z);
      const [rayIx, rayIy, rayIz] = intersect3D(
        i0x,
        i0y,
        i0z,
        rayX,
        rayY,
        rayZ,
        p0x,
        p0y,
        p0z,
        sideX,
        sideY,
        sideZ,
        true,
      );
      if (!isNaN(rayIx)) {
        const distance = vec3Dist(rayIx, rayIy, rayIz, i0x, i0y, i0z);
        if (distance < firstIntersection.distance) {
          // The orientation of the side tells us if the point is inside or not.
          const [crossX, crossY, crossZ] = vec3Cross(rayX, rayY, rayZ, sideX, sideY, sideZ);
          const inside = vec3Dot(crossX, crossY, crossZ, nBx, nBy, nBz) < 0;
          firstIntersection = { distance, inside };
        }
      }
    }
  }
  return firstIntersection.inside;
}

// Find an intersection event between a half-edge and a plane.
function findPlaneEvent(arrangement: Arrangement, rayA: Ray): PropagationEvent {
  let firstEvent: PropagationEvent = { distance: Infinity };
  const halfA = rayA.half;
  const [aPosX, aPosY, aPosZ] = getPosition(halfA);
  const [aDirX, aDirY, aDirZ] = getDirection(halfA);
  arrangement.halfEdges.forEach((halfB) => {
    if (!halfB.twin?.origin?.dir) {
      return;
    }
    const planeB = getPlane(halfB);
    if (!planeB) {
      return;
    }
    const [ix, iy, iz] = intersectLine(planeB, aPosX, aPosY, aPosZ, aDirX, aDirY, aDirZ);
    if (isNaN(ix)) {
      return;
    }

    const [deltaX, deltaY, deltaZ] = vec3Sub(ix, iy, iz, aPosX, aPosY, aPosZ);
    const distance = vec3Dot(aDirX, aDirY, aDirZ, deltaX, deltaY, deltaZ);
    if (distance < DIST_EPSILON) {
      return;
    }
    // Discard if the line is coplanar.
    const { nX: nBx, nY: nBy, nZ: nBz } = planeB;
    if (vec3Dot(nBx, nBy, nBz, aDirX, aDirY, aDirZ) < DIST_EPSILON) {
      return;
    }
    if (distance < firstEvent.distance) {
      // Check that the ray intersects the face. Cast a ray from the intersection
      // point and see how many sides of the face it intersects. If it is odd,
      // the ray intersects the face.
      const positions = findFacePositions(halfB);
      if (isPointInsideFace(positions, planeB, ix, iy, iz)) {
        firstEvent = { position: [ix, iy, iz], distance, halfA, halfB, plane: planeB };
      }
    }
  });
  return firstEvent;
}

function applyPlaneEvent(arrangement: Arrangement, event: PropagationEvent) {
  const { halfA } = event;
  if (!halfA) {
    return;
  }
  const intersection = event.position;
  const planeB = event.plane;
  const planeL = getPlane(halfA);
  const planeR = halfA.twin?.next ? getPlane(halfA.twin.next) : null;
  if (!planeL || !planeR || !planeB || !intersection) {
    return;
  }
  const leftLine = intersectPlanes(planeB, planeL);
  const rightLine = intersectPlanes(planeB, planeR);
  if (!leftLine || !rightLine) {
    return;
  }

  let [lDirX, lDirY, lDirZ] = leftLine.dir;
  let [rDirX, rDirY, rDirZ] = rightLine.dir;
  const reflex = isReflex(halfA);
  if (vec3Dot(lDirX, lDirY, lDirZ, planeR.nX, planeR.nY, planeR.nZ) > 0 !== reflex) {
    [lDirX, lDirY, lDirZ] = vec3Scale(lDirX, lDirY, lDirZ, -1);
  }
  if (vec3Dot(rDirX, rDirY, rDirZ, planeL.nX, planeL.nY, planeL.nZ) > 0 !== reflex) {
    [rDirX, rDirY, rDirZ] = vec3Scale(rDirX, rDirY, rDirZ, -1);
  }

  const vertex = createVertex(intersection, 3);
  addVertexToArrangement(arrangement, vertex);
  connectHalfs(arrangement, halfA, vertex.halfEdges[0]);
  if (vertex.halfEdges[2].twin?.origin) {
    vertex.halfEdges[2].twin.origin.dir = [lDirX, lDirY, lDirZ];
  }
  if (vertex.halfEdges[1].twin?.origin) {
    vertex.halfEdges[1].twin.origin.dir = [rDirX, rDirY, rDirZ];
  }

  const { nX: nRx, nY: nRy, nZ: nRz } = planeR;
  const { nX: nBx, nY: nBy, nZ: nBz } = planeB;
  const { nX: nLx, nY: nLy, nZ: nLz } = planeL;
  updateConvex(vertex.halfEdges[0], nRx, nRy, nRz);
  updateConvex(vertex.halfEdges[1], nBx, nBy, nBz);
  updateConvex(vertex.halfEdges[2], nLx, nLy, nLz);
}

function applyStraightEvent(arrangement: Arrangement, event: PropagationEvent) {
  const { halfA, halfB } = event;
  if (!halfA || !halfB) {
    return;
  }
  const planeA = getPlane(halfA);
  const planeB = getPlane(halfB);
  if (!planeA || !planeB) {
    return;
  }
  const { nX: ax, nY: ay, nZ: az } = planeA;
  const { nX: bx, nY: by, nZ: bz } = planeB;
  connectHalfs(arrangement, halfA, halfB);
  updateConvex(halfA, ax, ay, az);
  updateConvex(halfB, bx, by, bz);
  // halfA or halfB could have switched directions if it was bidirectional.
  // Update the next half-edges just in case.
  halfA.next && updateConvex(halfA.next, ax, ay, az);
  halfB.next && updateConvex(halfB.next, bx, by, bz);
}

function applyIntersectEvent(arrangement: Arrangement, event: PropagationEvent) {
  const { halfA, halfB } = event;
  const intersection = event.position;
  if (!halfA || !halfB || !intersection || !halfA.twin?.next || !halfB.twin?.next) {
    return;
  }

  // Two half-edges will intersect at a point. A third half-edge will be added in
  // the new direction. The new direction is othrogonal to planes A and B.
  const planeA = getPlane(halfA);
  const planeB = getPlane(halfB.twin.next);
  const planeC = getPlane(halfA.twin.next);
  if (!planeA || !planeB || !planeC) {
    return;
  }

  const { nX: ax, nY: ay, nZ: az } = planeA;
  const { nX: bx, nY: by, nZ: bz } = planeB;
  const { nX: cx, nY: cy, nZ: cz } = planeC;
  let [newX, newY, newZ] = vec3Cross(bx, by, bz, ax, ay, az);
  [newX, newY, newZ] = vec3Normalize(newX, newY, newZ);
  // Flip the new direction if it points out instead of into plane C.
  if (vec3Dot(cx, cy, cz, newX, newY, newZ) > 0) {
    [newX, newY, newZ] = vec3Scale(newX, newY, newZ, -1);
  }

  // Add the vertex and connect its half-edges.
  const vertex = createVertex(intersection, 3);
  addVertexToArrangement(arrangement, vertex);
  connectHalfs(arrangement, halfA, vertex.halfEdges[1]);
  connectHalfs(arrangement, halfB, vertex.halfEdges[2]);
  connectHalfEdges(vertex);

  const twin0 = vertex.halfEdges[0].twin;
  if (!twin0?.origin || !twin0?.next) {
    return;
  }
  twin0.origin.dir = [newX, newY, newZ];
  updateConvex(vertex.halfEdges[0], ax, ay, az);
  updateConvex(twin0.next, bx, by, bz);

  // If this is bidirectional, point the new direction upwards.
  if (isBidirectional(vertex.halfEdges[0]) && newZ < 0) {
    [newX, newY, newZ] = vec3Scale(newX, newY, newZ, -1);
    twin0.origin.dir = [newX, newY, newZ];
    updateConvex(vertex.halfEdges[0], ax, ay, az);
    updateConvex(twin0.next, bx, by, bz);
  }

  updateConvex(halfA, ax, ay, az);
  updateConvex(halfB, cx, cy, cz);

  // The intersection point could be at the origin of a half-edge meaning that the
  // edge has a length of 0. In that case, merge the two vertices.
  if (getEdgeLength(halfA) < DIST_EPSILON) {
    mergeWithTwin(halfA);
    removeHalfsFromArrangement(arrangement, [halfA, halfA.twin]);
  }
  if (getEdgeLength(halfB) < DIST_EPSILON) {
    mergeWithTwin(halfB);
    removeHalfsFromArrangement(arrangement, [halfB, halfB.twin]);
  }
}

// Apply the propagation event.
function applyPropagationEvent(arrangement: Arrangement, event: PropagationEvent) {
  if (event.plane) {
    applyPlaneEvent(arrangement, event);
  } else if (event.straight) {
    applyStraightEvent(arrangement, event);
  } else if (event.position) {
    applyIntersectEvent(arrangement, event);
  }
}

// Iterate one step.
function findPropagationEvent(arrangement: Arrangement, ray: Ray): PropagationEvent {
  const position: Vec3 = [0, 0, Infinity];
  let firstEvent: PropagationEvent = { position, distance: Infinity };

  const half = ray.half;
  // Ignore rays pointed downwards.
  if (getDirection(half)[2] < -DIST_EPSILON) {
    return firstEvent;
  }
  if (!half.twin || !half.twin.next) {
    return firstEvent;
  }

  const prev = getMostPrevious(half);
  const next = getMostNext(half.twin);

  if (!prev.twin) {
    return firstEvent;
  }

  const prevStraight = prev.twin ? findStraightEvent(prev.twin, half) : null;
  const nextStraight = findStraightEvent(half, next);

  const propagatePrev = prevStraight || findIntersectEvent(ray, prev.twin);
  const propagateNext = nextStraight || findIntersectEvent(ray, next);

  // Switch left and right half-edges.
  const temp = propagatePrev.halfA;
  propagatePrev.halfA = propagatePrev.halfB;
  propagatePrev.halfB = temp;

  firstEvent = compareEvents(firstEvent, propagatePrev);
  firstEvent = compareEvents(firstEvent, propagateNext);

  // TODO: Optimize this part.
  // Usually prev and next are the only half-edges we need to check, but if we have a
  // plane propagation event this splits the face into two parts. This checks every
  // half-edge (including prev and next). There should be a way to optimize this.
  const rightPlane = getPlane(half.twin.next);
  if (!rightPlane) {
    return firstEvent;
  }
  for (let i = 0; i < arrangement.halfEdges.length; i++) {
    const halfI = arrangement.halfEdges[i];
    const planeI = getPlane(halfI);
    if (!halfI.twin?.origin?.dir || halfI === half || !planeI) {
      continue;
    }
    if (isCoplanar(rightPlane, planeI)) {
      const propagateI = findIntersectEvent(ray, halfI);
      firstEvent = compareEvents(firstEvent, propagateI);
      const straightI = findStraightEvent(halfI, ray.half);
      firstEvent = compareEvents(firstEvent, straightI);
    }
  }

  const propagatePlane = findPlaneEvent(arrangement, ray);
  firstEvent = compareEvents(firstEvent, propagatePlane);
  return firstEvent;
}

// Iterate one step.
function propagateNextStep(arrangement: Arrangement): boolean {
  let nextEvent: PropagationEvent = { position: [Infinity, Infinity, Infinity], distance: Infinity };
  for (let i = 0; i < arrangement.halfEdges.length; i++) {
    const half = arrangement.halfEdges[i];
    if (!half.twin?.origin?.dir) {
      continue;
    }
    const bidirectional = isBidirectional(half);
    const ray = { half, bidirectional };
    const event = findPropagationEvent(arrangement, ray);
    if (compareEvents(nextEvent, event) === event) {
      if (event.plane || event.straight) {
        nextEvent = event;
      } else {
        const oppositeHalf = event.halfA === half ? event.halfB : event.halfA;
        if (!oppositeHalf) {
          continue;
        }
        const oppositeEvent = findPropagationEvent(arrangement, { half: oppositeHalf, bidirectional });
        if (!event.position || !oppositeEvent.position) {
          continue;
        }
        const [px0, py0, pz0] = event.position;
        const [px1, py1, pz1] = oppositeEvent.position;
        // Check that you get the same event if you swap halfs. This resolves an ambuity when
        // the lines are horizontal.
        if (vec3Dist(px0, py0, pz0, px1, py1, pz1) < DIST_EPSILON) {
          nextEvent = event;
        }
      }
    }
  }
  if (nextEvent.distance < Infinity) {
    applyPropagationEvent(arrangement, nextEvent);
    return true;
  } else {
    return false;
  }
}

function roofAutoComplete(arrangement: Arrangement) {
  let propagated = true;
  let count = 0;
  while (propagated) {
    propagated = propagateNextStep(arrangement);
    count++;
    // console.log('Step '+ count);
    // console.log(exportToString(arrangement));
  }
}

export default roofAutoComplete;
